import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { User, Mail, ArrowLeft, Edit, Loader2 } from 'lucide-react';
import { getApiUrl } from '../utils/api';

interface UserDetail {
  id: string;
  email: string;
  userType: string;
  status: string;
  emailVerified: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
  client?: { id: string; firstName: string; lastName: string; email: string } | null;
  admin?: { id: string; name: string; email: string; role: string; companyId: string | null } | null;
  practitioner?: { id: string; firstName: string; lastName: string; email: string } | null;
}

export function UserViewPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [user, setUser] = useState<UserDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) fetchUser();
  }, [id]);

  const fetchUser = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`/api/admin/users/${id}`), {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const data = await response.json();
        setUser(data.data.user);
      } else {
        navigate('/users');
      }
    } catch {
      navigate('/users');
    } finally {
      setLoading(false);
    }
  };

  const getUserTypeLabel = (userType: string) => {
    switch (userType) {
      case 'ADMIN': return 'Admin';
      case 'CLIENT': return 'Client';
      case 'PRACTITIONER': return 'Praticien';
      default: return userType;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'Actif';
      case 'INACTIVE': return 'Inactif';
      case 'SUSPENDED': return 'Suspendu';
      case 'LOCKED': return 'Verrouillé';
      default: return status;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="p-6 max-w-2xl">
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate('/users')}
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour à la liste
        </button>
        <Link
          to={`/users/${user.id}/edit`}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <Edit className="w-4 h-4" />
          Modifier
        </Link>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-3">
          <div className="p-2 bg-primary-100 rounded-lg">
            <User className="w-6 h-6 text-primary-600" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Utilisateur</h1>
            <p className="text-sm text-gray-500">{user.email}</p>
          </div>
        </div>
        <div className="px-6 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Email</label>
            <p className="mt-1 flex items-center gap-2 text-gray-900">
              <Mail className="w-4 h-4 text-gray-400" />
              {user.email}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Type de compte</label>
            <p className="mt-1">{getUserTypeLabel(user.userType)}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Statut</label>
            <p className="mt-1">
              <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${
                user.status === 'ACTIVE' ? 'bg-green-100 text-green-800' :
                user.status === 'INACTIVE' ? 'bg-gray-100 text-gray-800' :
                user.status === 'SUSPENDED' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
              }`}>
                {getStatusLabel(user.status)}
              </span>
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Email vérifié</label>
            <p className="mt-1">{user.emailVerified ? 'Oui' : 'Non'}</p>
          </div>
          {user.lastLoginAt && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Dernière connexion</label>
              <p className="mt-1 text-gray-900">{new Date(user.lastLoginAt).toLocaleString('fr-FR')}</p>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700">Profil lié</label>
            <div className="mt-1">
              {user.admin && (
                <Link to={`/admins/${user.admin.id}/edit`} className="text-primary-600 hover:underline">
                  Admin : {user.admin.name} ({user.admin.role})
                </Link>
              )}
              {user.client && (
                <Link to={`/clients/${user.client.id}`} className="text-primary-600 hover:underline">
                  Client : {user.client.firstName} {user.client.lastName}
                </Link>
              )}
              {user.practitioner && (
                <Link to={`/practitioners/${user.practitioner.id}`} className="text-primary-600 hover:underline">
                  Praticien : {user.practitioner.firstName} {user.practitioner.lastName}
                </Link>
              )}
              {!user.admin && !user.client && !user.practitioner && <span className="text-gray-500">Aucun</span>}
            </div>
          </div>
          <div className="pt-4 border-t border-gray-200 text-sm text-gray-500">
            Créé le {new Date(user.createdAt).toLocaleString('fr-FR')} · Modifié le {new Date(user.updatedAt).toLocaleString('fr-FR')}
          </div>
        </div>
      </div>
    </div>
  );
}
